<script setup>
import BaseModal from "@/components/global/modals/BaseModal.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import ModalHeading from "@/components/global-themed/ui/modals/ModalHeading.vue";
import ModalHeader from "@/components/global-themed/ui/modals/ModalHeader.vue";
import {computed} from "vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import Swal from "sweetalert2";

const emits = defineEmits(['close']);
const props = defineProps({
  userType: {
    type: String,
    required: true,
  },
  currency: {
    type: String,
    required: true,
  },
  billingPeriod: {
    type: String,
    required: true,
  }
})

const headerText = computed(() => {
  if (props.userType === 'SINGLE') {
    return 'Let Kit Handle Your SEO'
  }

  return 'Let Kit Handle Your Client\'s SEO'
});

const singleUserDiscountPrice = computed(() => {
  if (props.billingPeriod === 'MONTH') {
    return props.currency === 'GBP' ? '£85' : '$125';
  }
  return props.currency === 'GBP' ? '£611' : '$733';
})

const {mutate: requestUpgrade, isPending} = useMutation({
  mutationFn: async () => {
    return await axios.post('/ajax/v2/request-kit-upgrade');
  },
  onSuccess: () => {
    emits('close')
    Swal.fire({
      icon: 'success',
      title: 'You’re All Set – Kit is Now Active!',
      html: `You’ve successfully upgraded. Kit will now start working behind the scenes to manage your SEO and the Hike platform for you, supported by our account managers.
        <br>
        <br>
        Next, you’ll notice a few changes in the platform—including a short tour to show you what’s new. You’ll also get an email from Kit shortly outlining the next steps and what to expect in the coming days and weeks.`,
      confirmButtonColor: '#2F52E0',
      confirmButtonText: 'Ok, Got It',
      preConfirm: () => {
        window.location.reload();
      }
    })
  }
})

const purchaseKit = () => {
  if (props.userType === 'SINGLE') {
    requestUpgrade();
    return;
  }
  emits('close')
  window.addonPurchaser.purchaseKit();
}

</script>

<template>
  <Teleport
    to=".tailwind"
  >
    <BaseModal
      @close="$emit('close')"
    >
      <ModalContentWrapper>
        <div class="tw-flex tw-flex-col tw-gap-8 tw-text-lg tw-text-neutral-900">
          <ModalHeader>
            <ModalHeading>{{ headerText }}</ModalHeading>
          </ModalHeader>

          <div
            v-if="userType === 'SINGLE'"
            class="tw-flex tw-flex-col tw-gap-2"
          >
            <span>
              Kit is your built-in SEO expert, designed to manage your SEO tasks efficiently. Here’s what Kit will do for you:
            </span>
            <ul class="tw-list-disc tw-list-inside">
              <li>Fully optimize <strong>3 x pages per month</strong> via the Onsite Optimizer</li>
              <li>Research and create <strong>1 x blog post per month</strong></li>
              <li>Manage and optimize your <strong>Google Business Profile</strong></li>
              <li>Ensure your business is listed across <strong>all major local directories</strong></li>
              <li>Provide <strong>monthly SEO reports</strong> to track your progress</li>
              <li>Monthly calls with a <strong>dedicated (human) account manager</strong></li>
            </ul>
            <span>All this is done by Kit through the platform and the Onsite Optimizer — no need to lift a finger.</span>
            <span>Upgrade by the 15th May to enjoy <strong>15% off your Kit plan for life.</strong></span>
            <span class="tw-text-sm tw-self-center">(Your new {{ billingPeriod === 'MONTH' ? 'monthly' : 'annual' }} price would be {{ singleUserDiscountPrice }}, excluding any other purchased addons)</span>
          </div>

          <div
            v-else
            class="tw-flex tw-flex-col tw-gap-2"
          >
            <span>
              Kit is your new agency SEO employee, designed to manage your client's SEO tasks efficiently. Here’s what Kit will do for your client:
            </span>
            <ul class="tw-list-disc tw-list-inside">
              <li>Fully optimize <strong>3 x pages per month</strong> via the Onsite Optimizer</li>
              <li>Research and create <strong>1 x blog post per month</strong></li>
              <li>Manage and optimize their <strong>Google Business Profile</strong></li>
              <li>Ensure their business is listed across <strong>all major local directories</strong></li>
              <li>Provide <strong>monthly SEO reports</strong> to track their progress</li>
              <li>Monthly calls with a Hike <strong>dedicated (human) account manager</strong></li>
            </ul>
            <span>All this is done by Kit through the platform and the Onsite Optimizer — no need to lift a finger.</span>
            <span>Upgrade by the 15th May to enjoy <strong>15% off your Kit plan for life.</strong></span>
          </div>

          <div class="tw-flex tw-justify-between">
            <DefaultButton
              variant="primary-inverted"
              @click="$emit('close')"
            >
              Maybe Later
            </DefaultButton>
            <DefaultButton
              :loading="isPending"
              :disabled="isPending"
              @click="purchaseKit"
            >
              Upgrade Now — 15% Off
            </DefaultButton>
          </div>
        </div>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>
</template>
